/**
 * discussionsSlice — department assessment threads, backed by the generic
 * Discussion API:
 *   GET    api/discussion?relatedObjectType=ImportedDocumentBlock&relatedObjectId={docGuid}
 *   POST   api/discussion/message   (get-or-create the thread, then append)
 *   DELETE api/discussion/message/{id}
 *
 * Each imported-document fragment is one discussion, anchored by
 * relatedChildObjectId = blockKey. Messages are stored per blockKey so the
 * assess panel (thread view) and the dept-column summary read off the same map.
 */

import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { AppDataState, AppThunkAPIType, unknownError } from "../../../app/appTypes"
import { RootState } from "../../../app/store"
import { getWithAuth, postWithAuth, putWithAuth, deleteWithAuth } from "../../../http"

export const RELATED_OBJECT_TYPE = "ImportedDocumentBlock"

export interface DiscussionMessage {
    id: number
    discussionId: number
    parentMessageId: number | null
    content: string
    createdBy: string
    createdByFullName: string
    createdDate: string
}

export interface DiscussionMemberDto {
    userId: string
    fullName: string
    position: string
    email: string
}

export interface LoadDiscussionMembersArgs {
    relatedObjectType: string
    relatedObjectId: string
}

interface DiscussionDto {
    id: number
    guid: string
    title: string | null
    status: string
    discussionType: string
    relatedObjectType: string
    relatedObjectId: string
    relatedChildObjectId: string | null
    messages: DiscussionMessage[]
}

interface Thread {
    discussionId: number
    messages: DiscussionMessage[]
}

interface DiscussionsState {
    relatedObjectId: string | null
    byBlockKey: Record<string, Thread>
    loaded: AppDataState
}

const initialState: DiscussionsState = {
    relatedObjectId: null,
    byBlockKey: {},
    loaded: { type: 'empty' },
}

// Block keys are lowercase GUIDs; normalize defensively on both store & lookup.
const norm = (key: string) => key.toLowerCase()

export const loadDiscussions = createAsyncThunk<DiscussionDto[], { relatedObjectId: string }, AppThunkAPIType>(
    "discussions/load",
    async ({ relatedObjectId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/discussion?relatedObjectType=${RELATED_OBJECT_TYPE}&relatedObjectId=${relatedObjectId}`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DiscussionDto[]
        }
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
)

export const loadDiscussionMembers = createAsyncThunk<DiscussionMemberDto[], LoadDiscussionMembersArgs, AppThunkAPIType>(
    "discussions/load-members",
    async ({ relatedObjectType, relatedObjectId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/discussion/members?relatedObjectType=${relatedObjectType}&relatedObjectId=${relatedObjectId}`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DiscussionMemberDto[]
        }
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
)

export const postDiscussionMessage = createAsyncThunk<
    DiscussionMessage,
    { relatedObjectId: string; blockKey: string; content: string; parentMessageId?: number | null },
    AppThunkAPIType
>(
    "discussions/post-message",
    async ({ relatedObjectId, blockKey, content, parentMessageId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/discussion/message`,
            payload: {
                relatedObjectType: RELATED_OBJECT_TYPE,
                relatedObjectId,
                relatedChildObjectId: blockKey,
                discussionType: "PRIVATE_COMPANY",
                content,
                parentMessageId: parentMessageId ?? null,
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DiscussionMessage
        }
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
)

export const editDiscussionMessage = createAsyncThunk<
    DiscussionMessage,
    { messageId: number; blockKey: string; content: string },
    AppThunkAPIType
>(
    "discussions/edit-message",
    async ({ messageId, content }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/discussion/message/${messageId}`,
            payload: { content },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DiscussionMessage
        }
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
)

export const deleteDiscussionMessage = createAsyncThunk<
    void,
    { messageId: number; blockKey: string },
    AppThunkAPIType
>(
    "discussions/delete-message",
    async ({ messageId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/discussion/message/${messageId}` }))
        if (deleteWithAuth.fulfilled.match(result)) {
            return
        }
        return rejectWithValue(result.payload ?? { kind: 'unknown' })
    }
)

const slice = createSlice({
    name: 'discussions',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(loadDiscussions.pending, (state, action) => {
            state.relatedObjectId = action.meta.arg.relatedObjectId
            state.loaded = { type: 'loading', requestId: action.meta.requestId }
        })
        builder.addCase(loadDiscussions.fulfilled, (state, action) => {
            state.loaded = { type: 'loaded' }
            const map: Record<string, Thread> = {}
            for (const d of action.payload) {
                if (!d.relatedChildObjectId) continue
                map[norm(d.relatedChildObjectId)] = { discussionId: d.id, messages: d.messages ?? [] }
            }
            state.byBlockKey = map
        })
        builder.addCase(loadDiscussions.rejected, (state, action) => {
            state.loaded = { type: 'error', error: action.payload ?? unknownError() }
        })
        builder.addCase(postDiscussionMessage.fulfilled, (state, action) => {
            const key = norm(action.meta.arg.blockKey)
            const thread = state.byBlockKey[key] ?? { discussionId: action.payload.discussionId, messages: [] }
            thread.messages.push(action.payload)
            thread.discussionId = action.payload.discussionId
            state.byBlockKey[key] = thread
        })
        builder.addCase(editDiscussionMessage.fulfilled, (state, action) => {
            const key = norm(action.meta.arg.blockKey)
            const thread = state.byBlockKey[key]
            if (thread) {
                const i = thread.messages.findIndex(m => m.id === action.payload.id)
                if (i >= 0) thread.messages[i] = action.payload
            }
        })
        builder.addCase(deleteDiscussionMessage.fulfilled, (state, action) => {
            const key = norm(action.meta.arg.blockKey)
            const thread = state.byBlockKey[key]
            if (thread) {
                thread.messages = thread.messages.filter(m => m.id !== action.meta.arg.messageId)
            }
        })
    },
})

export default slice.reducer

const EMPTY: DiscussionMessage[] = []

export const selectDiscussionsLoaded = (state: RootState) => state.discussions.loaded

// The object the loaded discussions are anchored to (the document GUID). Used to
// post from places that don't carry the GUID as a prop (e.g. the edit-screen
// NotePanel reads it from the store rather than prop-drilling).
export const selectDiscussionsRelatedObjectId = (state: RootState) => state.discussions.relatedObjectId

export const selectMessagesForBlock = (state: RootState, blockKey: string): DiscussionMessage[] =>
    state.discussions.byBlockKey[norm(blockKey)]?.messages ?? EMPTY

// The thread's root message (no parent), if any. Used by the assess screen to
// keep a single thread per fragment: posts reply to the root instead of opening
// a new one. (This is an assess-screen UX rule, not a Discussion API constraint.)
export const selectRootMessageForBlock = (state: RootState, blockKey: string): DiscussionMessage | undefined =>
    (state.discussions.byBlockKey[norm(blockKey)]?.messages ?? EMPTY).find(m => m.parentMessageId === null)

// Orders a thread for display: the root (parentless) message first, then replies
// oldest→newest. If there's no root, returns the messages as-is (defensive).
export const orderThreadMessages = (messages: DiscussionMessage[]): DiscussionMessage[] => {
    const root = messages.find(m => m.parentMessageId === null)
    if (!root) return messages.slice()
    const replies = messages
        .filter(m => m.parentMessageId !== null)
        .slice()
        .sort((a, b) => a.createdDate.localeCompare(b.createdDate))
    return [root, ...replies]
}

export interface ThreadSummary {
    comment: string
    authorFullName: string
    count: number
}

// Per-block summary for the dept-column bubble: the ROOT (thread-starting)
// message + thread size. The root is what represents the fragment at a glance;
// replies only bump the count.
export const selectThreadSummaries = createSelector(
    [(s: RootState) => s.discussions.byBlockKey],
    (byBlockKey): Record<string, ThreadSummary> => {
        const out: Record<string, ThreadSummary> = {}
        for (const [key, thread] of Object.entries(byBlockKey)) {
            if (thread.messages.length === 0) continue
            // Prefer the parentless root; fall back to the earliest message.
            const root = thread.messages.find(m => m.parentMessageId === null)
                ?? thread.messages.reduce((a, b) => (a.createdDate <= b.createdDate ? a : b))
            out[key] = {
                comment: root.content,
                authorFullName: root.createdByFullName,
                count: thread.messages.length,
            }
        }
        return out
    },
)
